import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Archive, CheckCircle, Film, Loader2, RefreshCw, Save, Upload } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk

const blankVideoForm = () => ({
  slug: '',
  category_id: '',
  translations: {
    el: { title: '', description: '' },
    en: { title: '', description: '' }
  },
  thumbnail_url: '',
  duration_seconds: 0,
  is_free_preview: false,
  preview_seconds: 60,
  is_published: false,
  is_featured: false,
  is_ppv_enabled: false,
  ppv_price: 0,
  ppv_currency: 'EUR',
  ppv_provider: 'both',
  publish_at: null
});

const adminInputClass =
  "w-full bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminTextareaClass =
  "w-full min-h-[90px] bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminSelectTriggerClass =
  "w-full bg-misel-card border border-white/15 text-white rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminSelectContentClass =
  "z-[9999] bg-[#111111] border border-white/15 text-white shadow-xl";
const adminSelectItemClass =
  "text-white focus:bg-primary/20 focus:text-white";

const normalizeTranslations = (video) => ({
  el: {
    title: video?.translations?.el?.title || video?.title || '',
    description: video?.translations?.el?.description || video?.description || ''
  },
  en: {
    title: video?.translations?.en?.title || '',
    description: video?.translations?.en?.description || ''
  }
});

const formFromVideo = (video) => {
  if (!video) return blankVideoForm();
  return {
    slug: video.slug || '',
    category_id: video.category_id || '',
    translations: normalizeTranslations(video),
    thumbnail_url: video.thumbnail_url || '',
    duration_seconds: video.duration_seconds || 0,
    is_free_preview: !!video.is_free_preview,
    preview_seconds: video.preview_seconds || 60,
    is_published: !!video.is_published,
    is_featured: !!video.is_featured,
    is_ppv_enabled: !!video.is_ppv_enabled,
    ppv_price: video.ppv_price || 0,
    ppv_currency: video.ppv_currency || 'EUR',
    ppv_provider: video.ppv_provider || 'both',
    publish_at: video.publish_at || null
  };
};

const categoryLabel = (category) => (
  category.title ||
  category.translations?.el?.title ||
  category.translations?.en?.title ||
  category.slug ||
  category.id
);

const VideoUploadDialog = ({
  open,
  onOpenChange,
  video,
  videoId,
  categories = [],
  token,
  onUploadComplete,
  initialTab = 'details'
}) => {
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentVideo, setCurrentVideo] = useState(video || null);
  const [form, setForm] = useState(blankVideoForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [transcodeStatus, setTranscodeStatus] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  const currentVideoId = currentVideo?.id || video?.id || videoId;

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab || 'details');
    setCurrentVideo(video || (videoId ? { id: videoId } : null));
    setForm(formFromVideo(video));
    setProgress(0);
    setError(null);
    setSuccess(false);
  }, [open, video, videoId, initialTab]);

  useEffect(() => {
    if (open && currentVideoId) {
      fetchTranscodeStatus(currentVideoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentVideoId]);

  const headers = { Authorization: `Bearer ${token}` };

  const resetUploadState = () => {
    setUploading(false);
    setProgress(0);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) resetUploadState();
    onOpenChange?.(isOpen);
  };

  const updateTranslation = (locale, field, value) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [locale]: {
          ...prev.translations[locale],
          [field]: value
        }
      }
    }));
  };

  const buildPayload = (allowPublish) => ({
    slug: form.slug,
    category_id: form.category_id,
    translations: form.translations,
    thumbnail_url: form.thumbnail_url || null,
    duration_seconds: parseInt(form.duration_seconds, 10) || 0,
    is_free_preview: !!form.is_free_preview,
    preview_seconds: parseInt(form.preview_seconds, 10) || 0,
    is_published: allowPublish ? !!form.is_published : false,
    is_ppv_enabled: !!form.is_ppv_enabled,
    ppv_price: parseFloat(form.ppv_price) || 0,
    ppv_currency: form.ppv_currency || 'EUR',
    ppv_provider: form.ppv_provider || 'both',
    publish_at: form.publish_at || null
  });

  const saveVideo = async () => {
    if (!form.slug || !form.category_id) {
      toast.error('Slug and category are required');
      return;
    }

    setSaving(true);
    try {
      let savedId = currentVideoId;
      if (savedId) {
        await axios.put(`${API}/admin/videos/${savedId}`, buildPayload(true), { headers });
      } else {
        const res = await axios.post(`${API}/admin/videos`, buildPayload(false), { headers });
        savedId = res.data.id;
        setCurrentVideo({ id: savedId, slug: res.data.slug, is_featured: false });
        setForm((prev) => ({ ...prev, is_published: false }));
      }

      if (savedId && !!currentVideo?.is_featured !== !!form.is_featured) {
        await axios.put(`${API}/admin/videos/${savedId}/featured?is_featured=${!!form.is_featured}`, {}, { headers });
        setCurrentVideo((prev) => ({ ...(prev || {}), id: savedId, is_featured: !!form.is_featured }));
      }

      toast.success(currentVideoId ? 'Video updated' : 'Video created');
      onUploadComplete?.();
      if (savedId) fetchTranscodeStatus(savedId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const archiveVideo = async () => {
    if (!currentVideoId) return;
    if (!window.confirm('Archive this video? Existing purchases/history are preserved.')) return;
    try {
      await axios.delete(`${API}/admin/videos/${currentVideoId}`, { headers });
      toast.success('Video archived');
      onUploadComplete?.();
      handleOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive video');
    }
  };

  const fetchTranscodeStatus = async (id = currentVideoId) => {
    if (!id) {
      setTranscodeStatus(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/videos/${id}/transcode-status`, { headers });
      setTranscodeStatus(res.data);
    } catch (err) {
      setTranscodeStatus({ status: 'error', detail: err.response?.data?.detail || 'Failed to load status' });
    }
  };

  const uploadThumbnail = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setThumbnailUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/uploads/thumbnail`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setForm((prev) => ({ ...prev, thumbnail_url: res.data?.url || prev.thumbnail_url }));
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  const retryTranscode = async () => {
    if (!currentVideoId) return;
    setRetrying(true);
    try {
      const res = await axios.post(`${API}/admin/videos/${currentVideoId}/transcode-retry`, {}, { headers });
      toast.success('Transcoding retry queued');
      setTranscodeStatus(res.data);
      setCurrentVideo((prev) => ({ ...(prev || {}), id: currentVideoId, source_file: res.data.source_file, transcode_status: 'queued' }));
      onUploadComplete?.();
      fetchTranscodeStatus(currentVideoId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry transcoding');
    } finally {
      setRetrying(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!currentVideoId) {
      setError('Save the video metadata before uploading a file.');
      return;
    }

    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Supported: MP4, MOV, AVI, WebM, MKV.');
      return;
    }

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    setUploading(true);
    setProgress(0);
    setError(null);
    setSuccess(false);

    try {
      // 1. Init upload session
      const initRes = await axios.post(
        `${API}/admin/videos/${currentVideoId}/upload/init`,
        { filename: file.name, content_type: file.type, total_size: file.size, total_chunks: totalChunks },
        { headers }
      );
      const uploadId = initRes.data.upload_id;

      // 2. Send chunks
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
        const fd = new FormData();
        fd.append('chunk_index', String(i));
        fd.append('chunk', chunk, file.name);
        await axios.post(`${API}/admin/videos/${currentVideoId}/upload/chunk/${uploadId}`, fd, { headers });
        setProgress(Math.round(((i + 1) / totalChunks) * 95));
      }

      // 3. Complete — merge and queue transcode
      await axios.post(`${API}/admin/videos/${currentVideoId}/upload/complete/${uploadId}`, {}, { headers });
      setProgress(100);
      setSuccess(true);
      toast.success('Video uploaded. Transcoding has been queued.');
      onUploadComplete?.();
      fetchTranscodeStatus(currentVideoId);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Upload failed';
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const statusLabel = transcodeStatus?.status || form.transcode_status || currentVideo?.transcode_status || 'no_job';
  const hasSourceFile = !!(currentVideo?.source_file || transcodeStatus?.input_path || transcodeStatus?.source_file);
  const canRetryTranscode = currentVideoId && ['failed', 'error'].includes(String(statusLabel).toLowerCase()) && hasSourceFile;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-misel-card border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-roboto font-bold flex items-center gap-2">
            <Film className="text-primary" size={20} />
            {currentVideoId ? 'Edit Video' : 'New Video'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-5 bg-white/5">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="publishing">Publishing</TabsTrigger>
            <TabsTrigger value="paywall">PPV</TabsTrigger>
            <TabsTrigger value="upload">Upload</TabsTrigger>
            <TabsTrigger value="status">Status</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">Slug</label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={`${adminInputClass} mt-1`} />
              </div>
              <div>
                <label className="text-sm text-gray-400">Category</label>
                <Select value={form.category_id} onValueChange={(value) => setForm({ ...form, category_id: value })}>
                  <SelectTrigger className={`${adminSelectTriggerClass} mt-1`}>
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    {categories.map((category) => (
                      <SelectItem className={adminSelectItemClass} key={category.id} value={category.id}>
                        {categoryLabel(category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Tabs defaultValue="el">
              <TabsList className="bg-white/5">
                <TabsTrigger value="el">Greek</TabsTrigger>
                <TabsTrigger value="en">English</TabsTrigger>
              </TabsList>
              <TabsContent value="el" className="space-y-4 mt-4">
                <Input value={form.translations.el.title} onChange={(e) => updateTranslation('el', 'title', e.target.value)} placeholder="Greek title" className={adminInputClass} />
                <textarea value={form.translations.el.description} onChange={(e) => updateTranslation('el', 'description', e.target.value)} placeholder="Greek description" className={adminTextareaClass} />
              </TabsContent>
              <TabsContent value="en" className="space-y-4 mt-4">
                <Input value={form.translations.en.title} onChange={(e) => updateTranslation('en', 'title', e.target.value)} placeholder="English title" className={adminInputClass} />
                <textarea value={form.translations.en.description} onChange={(e) => updateTranslation('en', 'description', e.target.value)} placeholder="English description" className={adminTextareaClass} />
              </TabsContent>
            </Tabs>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">Thumbnail URL</label>
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <Input value={form.thumbnail_url || ''} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className={adminInputClass} />
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    onChange={uploadThumbnail}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={thumbnailUploading}
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="border-white/20 text-white hover:bg-white/10 min-h-[44px] shrink-0"
                  >
                    {thumbnailUploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                    Upload Thumbnail
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400">Duration seconds</label>
                <Input type="number" value={form.duration_seconds} onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })} className={`${adminInputClass} mt-1`} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="publishing" className="space-y-4 mt-6">
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 text-white bg-white/5 rounded-md p-3">
                <input type="checkbox" checked={!!form.is_free_preview} onChange={(e) => setForm({ ...form, is_free_preview: e.target.checked })} />
                Free preview flag
              </label>
              <div>
                <label className="text-sm text-gray-400">Preview seconds</label>
                <Input type="number" value={form.preview_seconds} onChange={(e) => setForm({ ...form, preview_seconds: e.target.value })} className={`${adminInputClass} mt-1`} />
              </div>
              <label className="flex items-center gap-2 text-white bg-white/5 rounded-md p-3">
                <input type="checkbox" checked={!!form.is_published} disabled={!currentVideoId} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                Published
              </label>
              <label className="flex items-center gap-2 text-white bg-white/5 rounded-md p-3">
                <input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                Featured
              </label>
            </div>
            {!currentVideoId && (
              <p className="text-xs text-yellow-400">New videos are saved unpublished first. Upload and verify HLS before publishing.</p>
            )}
          </TabsContent>

          <TabsContent value="paywall" className="space-y-4 mt-6">
            <div className="bg-white/5 border border-white/10 rounded-md p-4 space-y-4">
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={!!form.is_ppv_enabled}
                  onChange={(e) => setForm({ ...form, is_ppv_enabled: e.target.checked })}
                />
                Enable pay-per-view for this video
              </label>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-400">PPV price</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.ppv_price}
                    onChange={(e) => setForm({ ...form, ppv_price: e.target.value })}
                    className={`${adminInputClass} mt-1`}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">Currency</label>
                  <Select value={form.ppv_currency} onValueChange={(value) => setForm({ ...form, ppv_currency: value })}>
                    <SelectTrigger className={`${adminSelectTriggerClass} mt-1`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      <SelectItem className={adminSelectItemClass} value="EUR">EUR</SelectItem>
                      <SelectItem className={adminSelectItemClass} value="USD">USD</SelectItem>
                      <SelectItem className={adminSelectItemClass} value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Provider</label>
                  <Select value={form.ppv_provider} onValueChange={(value) => setForm({ ...form, ppv_provider: value })}>
                    <SelectTrigger className={`${adminSelectTriggerClass} mt-1`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      <SelectItem className={adminSelectItemClass} value="both">Stripe + Revolut</SelectItem>
                      <SelectItem className={adminSelectItemClass} value="revolut">Revolut only</SelectItem>
                      <SelectItem className={adminSelectItemClass} value="stripe">Stripe only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Legacy videos without a PPV price continue to use the existing global fallback price.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="upload" className="space-y-4 mt-6">
            <div className={`border-2 border-dashed rounded-lg p-8 text-center ${uploading ? 'border-primary/50 bg-primary/5' : 'border-white/20 hover:border-primary/50'}`}>
              <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileSelect} disabled={uploading || !currentVideoId} className="hidden" id="video-upload-input" />
              {success ? (
                <div className="text-green-400">
                  <CheckCircle size={48} className="mx-auto mb-3" />
                  <p>Upload successful</p>
                </div>
              ) : uploading ? (
                <div>
                  <Loader2 size={48} className="mx-auto mb-3 text-primary animate-spin" />
                  <p className="text-gray-400 mb-2">Uploading...</p>
                  <div className="w-full bg-white/10 rounded-full h-3 mb-2 overflow-hidden">
                    <div className="bg-primary h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-sm text-white font-medium">{progress}%</p>
                </div>
              ) : (
                <label htmlFor="video-upload-input" className={currentVideoId ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}>
                  <Upload size={48} className="mx-auto mb-3 text-gray-500" />
                  <p className="text-gray-400 mb-2">{currentVideoId ? 'Click to upload or replace video file' : 'Save metadata before uploading video'}</p>
                  <p className="text-xs text-gray-600">MP4, MOV, AVI, WebM, MKV</p>
                  <p className="text-xs text-primary/70 mt-1">Chunked upload enabled — large files supported.</p>
                </label>
              )}
            </div>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
          </TabsContent>

          <TabsContent value="status" className="space-y-4 mt-6">
            <div className="bg-white/5 rounded-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Transcode status</p>
                  <p className="text-xl text-white font-semibold">{statusLabel}</p>
                </div>
                <Button variant="outline" onClick={() => fetchTranscodeStatus()} disabled={!currentVideoId} className="border-white/20 text-white hover:bg-white/10">
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </Button>
              </div>
              {transcodeStatus && (
                <div className="mt-4 bg-black/30 rounded p-3 text-xs text-gray-300 space-y-1">
                  <p><span className="text-gray-500">Job:</span> {transcodeStatus.id || transcodeStatus.job_id || 'latest'}</p>
                  <p><span className="text-gray-500">Created:</span> {transcodeStatus.created_at || '-'}</p>
                  <p><span className="text-gray-500">Source:</span> {transcodeStatus.input_path || transcodeStatus.source_file || currentVideo?.source_file || '-'}</p>
                  {transcodeStatus.error && <p className="text-red-300"><span className="text-gray-500">Error:</span> {transcodeStatus.error}</p>}
                  {transcodeStatus.detail && <p className="text-red-300"><span className="text-gray-500">Detail:</span> {transcodeStatus.detail}</p>}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {hasSourceFile ? 'Source file is available for retry.' : 'No source file is available for retry yet.'}
                </p>
                {canRetryTranscode && (
                  <Button
                    variant="outline"
                    onClick={retryTranscode}
                    disabled={retrying}
                    className="border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/10"
                  >
                    {retrying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
                    Retry Transcoding
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4">
              <p className="text-red-300 text-sm mb-3">Archive disables the video without deleting HLS/uploads/thumbnails or purchase history.</p>
              <Button variant="outline" onClick={archiveVideo} disabled={!currentVideoId} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                <Archive size={16} className="mr-2" />
                Archive Video
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between gap-3 pt-4 border-t border-white/10 mt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} className="border-white/20 text-white">
            Close
          </Button>
          <Button onClick={saveVideo} disabled={saving} className="bg-primary hover:bg-primary/90">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
            Save Video
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoUploadDialog;
